/**
 * AuthCallback - SSO Authentication Callback Handler
 *
 * This page handles the redirect back from centralized Nexus Auth authentication.
 * It checks if the user has a valid session and redirects accordingly.
 *
 * Flow:
 * 1. User visits protected route → redirected to Nexus Auth login
 * 2. User logs in at Nexus Auth → redirected back here with session cookie
 * 3. This page checks session via Nexus SDK
 * 4. If authenticated → redirect to intended destination or dashboard
 * 5. If not authenticated → redirect back to Nexus Auth login
 */
import { useEffect, useState } from 'react';
// Note: useState kept for hasChecked flag
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useExamAuth } from '@/hooks/useExamAuth';
import { LoadingScreen } from '@azyntor-labs/nexus-ui';
import { NEXUS_AUTH_CONFIG } from '../../config/nexusAuth';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, hasAnyRole } = useExamAuth();
  const [hasChecked, setHasChecked] = useState(false);

  // Get the intended redirect destination from query params
  const redirectTo = searchParams.get('redirect') || searchParams.get('returnUrl') || '/';

  useEffect(() => {
    // Prevent multiple redirects
    if (hasChecked) return;

    const handleCallback = () => {
      // Still loading - wait
      if (isLoading) {
        return;
      }

      setHasChecked(true);

      // If authenticated, redirect to appropriate dashboard
      if (isAuthenticated) {
        handleRedirect();
        return;
      }

      // Not authenticated - redirect to Nexus Auth login
      NEXUS_AUTH_CONFIG.redirectToLogin(redirectTo);
    };

    // Small delay to allow Nexus SDK to hydrate
    const timeout = setTimeout(handleCallback, 1000);
    return () => clearTimeout(timeout);
  }, [isAuthenticated, isLoading, hasChecked]);

  /**
   * Redirect authenticated user based on role
   */
  const handleRedirect = () => {
    // If we have a specific redirect destination (not root), use it
    if (redirectTo && redirectTo !== '/' && !redirectTo.includes('/auth/callback')) {
      // Handle full URLs vs paths
      if (redirectTo.startsWith('http')) {
        window.location.href = redirectTo;
        return;
      }
      navigate(redirectTo, { replace: true });
      return;
    }

    // Role-based redirect to appropriate dashboard
    if (hasAnyRole(['system_admin', 'super_admin'])) {
      navigate('/system-admin/dashboard', { replace: true });
    } else if (hasAnyRole(['admin'])) {
      navigate('/admin/dashboard', { replace: true });
    } else if (hasAnyRole(['instructor', 'teacher'])) {
      navigate('/teacher/dashboard', { replace: true });
    } else {
      navigate('/student/dashboard', { replace: true });
    }
  };

  // Show loading state
  return (
    <LoadingScreen
      message="Authenticating..."
      submessage="Verifying your session"
      variant="cyber"
    />
  );
}

export default AuthCallback;
